import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import {
  calculateBillableRevenue,
  getEntryContactName,
  getEntryTicketTitle,
  getEntryUserName,
  type ReportTicketRow,
  type ReportUserRow,
} from "./report-helpers";

export interface TimeReportFilters {
  date_from?: string;
  date_to?: string;
  user_id?: string;
  contact_id?: string;
  ticket_id?: string;
  is_approved?: boolean;
  entry_type?: string[];
}

interface TimeReportRow {
  id: string;
  description: string | null;
  hours: number | null;
  entry_type: string | null;
  billable_rate: number | null;
  date_worked: string;
  is_approved: boolean | null;
  ticket: ReportTicketRow | ReportTicketRow[] | null;
  user: ReportUserRow | ReportUserRow[] | null;
}

interface TimeMetricsRow {
  id: string;
  hours: number | null;
  billable_rate: number | null;
  entry_type: string | null;
  is_approved: boolean | null;
  date_worked: string;
  ticket:
    | { id: string; contact_id: string | null }
    | Array<{ id: string; contact_id: string | null }>
    | null;
}

// Filters are applied inline at each call site rather than via a generic
// helper. A generic `<T extends Filterable>` helper triggered TS2589
// (excessively deep type instantiation) when inferring T from Supabase's
// deeply-nested PostgrestFilterBuilder type. Direct chained reassignment on
// the original `query` variable preserves the rich Supabase return type
// without forcing TS to compare PostgrestFilterBuilder against a constraint.

export function useTimeReportData(
  filters: TimeReportFilters,
  isClientUser: boolean,
  clientId?: string
) {
  return useQuery({
    queryKey: ["time-reports", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getSupabase();

      // CLIENT-SAFE path: select ONLY work-transparency columns — no
      // billable_rate, no is_approved (internal workflow state), no app_users
      // join (which WTC employee logged it is internal). Revenue + employee
      // identity are suppressed in the output. RLS still scopes the rows.
      if (isClientUser) {
        let clientQuery = supabase.from("time_entries").select(`
                    id,
                    description,
                    hours,
                    entry_type,
                    date_worked,
                    ticket:tickets!inner(id, title, contact:contacts!inner(name, company_name))
                `);

        if (filters.date_from) clientQuery = clientQuery.gte("date_worked", filters.date_from);
        if (filters.date_to) clientQuery = clientQuery.lte("date_worked", filters.date_to);
        if (filters.ticket_id) clientQuery = clientQuery.eq("ticket_id", filters.ticket_id);
        if (filters.entry_type?.length)
          clientQuery = clientQuery.in("entry_type", filters.entry_type);
        if (clientId) clientQuery = clientQuery.eq("ticket.contact_id", clientId);

        const { data, error } = await clientQuery.order("date_worked", { ascending: false });
        if (error) {
          logger.error("Error fetching time reports", error);
          throw new Error("Failed to fetch time data");
        }

        const rows = (data || []) as Array<
          Omit<TimeReportRow, "billable_rate" | "is_approved" | "user">
        >;
        const processedData = rows.map((entry) => ({
          id: entry.id,
          user_name: null,
          contact_name: getEntryContactName(entry),
          ticket_title: getEntryTicketTitle(entry),
          date_worked: entry.date_worked,
          hours_worked: entry.hours,
          description: entry.description,
          is_approved: null,
          entry_type: entry.entry_type,
          revenue: null,
        }));

        return { data: processedData, total: processedData.length };
      }

      let query = supabase.from("time_entries").select(`
                    id,
                    description,
                    hours,
                    entry_type,
                    billable_rate,
                    date_worked,
                    is_approved,
                    ticket:tickets!inner(id, title, contact:contacts!inner(name, company_name)),
                    user:app_users!time_entries_user_id_fkey(email, first_name, last_name)
                `);

      if (filters.date_from) query = query.gte("date_worked", filters.date_from);
      if (filters.date_to) query = query.lte("date_worked", filters.date_to);
      if (filters.user_id) query = query.eq("user_id", filters.user_id);
      if (filters.contact_id) query = query.eq("ticket.contact_id", filters.contact_id);
      if (filters.ticket_id) query = query.eq("ticket_id", filters.ticket_id);
      if (filters.is_approved !== undefined)
        query = query.eq("is_approved", filters.is_approved);
      if (filters.entry_type?.length) query = query.in("entry_type", filters.entry_type);

      const { data, error } = await query.order("date_worked", { ascending: false });

      if (error) {
        logger.error("Error fetching time reports", error);
        throw new Error("Failed to fetch time data");
      }

      const rows = (data || []) as TimeReportRow[];
      const processedData = rows.map((entry) => ({
        id: entry.id,
        user_name: getEntryUserName(entry),
        contact_name: getEntryContactName(entry),
        ticket_title: getEntryTicketTitle(entry),
        date_worked: entry.date_worked,
        hours_worked: entry.hours,
        description: entry.description,
        is_approved: entry.is_approved,
        entry_type: entry.entry_type,
        revenue: calculateBillableRevenue(entry),
      }));

      return { data: processedData, total: processedData.length };
    },
    enabled: true,
  });
}

export function useTimeMetrics(
  filters: TimeReportFilters,
  isClientUser: boolean,
  clientId?: string
) {
  return useQuery({
    queryKey: ["time-metrics", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getSupabase();

      // CLIENT-SAFE path: hours-based metrics only. No billable_rate (→ no
      // revenue) and no is_approved (internal workflow) cross the wire.
      if (isClientUser) {
        let clientQuery = supabase.from("time_entries").select(`
                    id,
                    hours,
                    entry_type,
                    date_worked,
                    ticket:tickets!inner(id, contact_id)
                `);

        if (filters.date_from) clientQuery = clientQuery.gte("date_worked", filters.date_from);
        if (filters.date_to) clientQuery = clientQuery.lte("date_worked", filters.date_to);
        if (filters.ticket_id) clientQuery = clientQuery.eq("ticket_id", filters.ticket_id);
        if (filters.entry_type?.length)
          clientQuery = clientQuery.in("entry_type", filters.entry_type);
        if (clientId) clientQuery = clientQuery.eq("ticket.contact_id", clientId);

        const { data, error } = await clientQuery;
        if (error) {
          logger.error("Error fetching time metrics", error);
          throw new Error("Failed to fetch time metrics");
        }

        const rows = (data || []) as Array<Pick<TimeMetricsRow, "hours" | "entry_type">>;
        const totalHours = rows.reduce((sum, entry) => sum + (entry.hours || 0), 0);
        const billableHours = rows
          .filter((entry) => entry.entry_type === "billable")
          .reduce((sum, entry) => sum + (entry.hours || 0), 0);
        const totalEntries = rows.length;

        return {
          totalHours,
          billableHours,
          nonBillableHours: totalHours - billableHours,
          totalEntries,
          approvedEntries: null,
          totalRevenue: null,
          approvalRate: null,
          billableRate: totalHours > 0 ? (billableHours / totalHours) * 100 : 0,
          // snake_case aliases the report framework's DefaultMetricsDisplay reads
          // (camelCase-only returns render a blank KPI row).
          total_count: totalEntries,
          total_hours: totalHours,
          total_revenue: null,
        };
      }

      let query = supabase.from("time_entries").select(`
                    id,
                    hours,
                    billable_rate,
                    entry_type,
                    is_approved,
                    date_worked,
                    ticket:tickets!inner(id, contact_id)
                `);

      if (filters.date_from) query = query.gte("date_worked", filters.date_from);
      if (filters.date_to) query = query.lte("date_worked", filters.date_to);
      if (filters.user_id) query = query.eq("user_id", filters.user_id);
      if (filters.contact_id) query = query.eq("ticket.contact_id", filters.contact_id);
      if (filters.ticket_id) query = query.eq("ticket_id", filters.ticket_id);
      if (filters.is_approved !== undefined)
        query = query.eq("is_approved", filters.is_approved);
      if (filters.entry_type?.length) query = query.in("entry_type", filters.entry_type);

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching time metrics", error);
        throw new Error("Failed to fetch time metrics");
      }

      const rows = (data || []) as TimeMetricsRow[];
      const totalHours = rows.reduce((sum, entry) => sum + (entry.hours || 0), 0);
      const billableHours = rows
        .filter((entry) => entry.entry_type === "billable")
        .reduce((sum, entry) => sum + (entry.hours || 0), 0);
      const nonBillableHours = totalHours - billableHours;
      const totalEntries = rows.length;
      const approvedEntries = rows.filter((entry) => entry.is_approved).length;
      const totalRevenue = rows
        .filter(
          (entry) =>
            entry.entry_type === "billable" && entry.billable_rate && entry.hours
        )
        .reduce(
          (sum, entry) => sum + (entry.billable_rate ?? 0) * (entry.hours ?? 0),
          0
        );

      return {
        totalHours,
        billableHours,
        nonBillableHours,
        totalEntries,
        approvedEntries,
        totalRevenue,
        approvalRate: totalEntries > 0 ? (approvedEntries / totalEntries) * 100 : 0,
        billableRate: totalHours > 0 ? (billableHours / totalHours) * 100 : 0,
        // snake_case aliases the report framework's DefaultMetricsDisplay reads
        // (camelCase-only returns render a blank KPI row).
        total_count: totalEntries,
        total_hours: totalHours,
        total_revenue: totalRevenue,
      };
    },
    enabled: true,
  });
}
